// Web Push on the tablet (TZ 12; STAGE_5 5.6): the crew is told about a new assignment
// even with the app in the background and the screen off. The subscription belongs to
// this device and this account; signing out gives it up.

import { api } from '@/api/client';

export type PushState = 'unsupported' | 'default' | 'granted' | 'denied';

export function pushState(): PushState {
  if (!('serviceWorker' in navigator) || !('PushManager' in window) || !('Notification' in window)) {
    return 'unsupported';
  }
  return Notification.permission;
}

/** The VAPID key as the browser wants it: raw bytes, not base64url. */
function keyBytes(key: string): ArrayBuffer {
  const padded = (key + '='.repeat((4 - (key.length % 4)) % 4)).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(padded);
  const bytes = new Uint8Array(new ArrayBuffer(raw.length));
  for (let at = 0; at < raw.length; at += 1) bytes[at] = raw.charCodeAt(at);
  return bytes.buffer;
}

function parts(subscription: PushSubscription): { endpoint: string; p256dh: string; auth: string } | null {
  const keys = subscription.toJSON().keys;
  if (!keys?.['p256dh'] || !keys['auth']) return null;
  return { endpoint: subscription.endpoint, p256dh: keys['p256dh'], auth: keys['auth'] };
}

/** Subscribe this tablet; `ask` lets the browser put its own question to the crew. */
export async function subscribeToPush(vapidKey: string, ask = false): Promise<PushState> {
  if (!vapidKey || pushState() === 'unsupported') return 'unsupported';
  let permission: PushState = Notification.permission;
  if (permission === 'default' && ask) permission = await Notification.requestPermission();
  if (permission !== 'granted') return permission;

  const registration = await navigator.serviceWorker.ready;
  const existing = await registration.pushManager.getSubscription();
  const subscription =
    existing ??
    (await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: keyBytes(vapidKey),
    }));
  const body = parts(subscription);
  if (body) await api.post('/me/push-subscription', { body });
  return 'granted';
}

/** Give up this tablet's subscription (the doctor is signing out). */
export async function unsubscribeFromPush(): Promise<void> {
  if (pushState() === 'unsupported') return;
  const registration = await navigator.serviceWorker.getRegistration();
  const subscription = await registration?.pushManager.getSubscription();
  const body = subscription ? parts(subscription) : null;
  if (!subscription || !body) return;
  try {
    await api.delete('/me/push-subscription', { body });
  } finally {
    await subscription.unsubscribe();
  }
}
